// 图片 URL 规范化 + 从 Game.pics 智能挑图
//
// 历史背景：以前生产环境 image.cloud.cgaapp.com 证书过期，浏览器拒载，所以走
// Vite 代理 /cdn-img/* 绕过证书校验。jzbro 测试环境的 image.jzbro.cn /
// profile.jzbro.cn 都有有效证书，原本可以直链，但保留代理路径有两个好处：
//   1) dev 跨域请求统一从 vite 端口出，避免某些浏览器隐私模式 cookie 限制
//   2) 切回老环境也能复用
//
// CDN_HOSTS 列表里的 host 都会被改写到 /cdn-img/*；其它直链。

import type { Game, Pic } from "@/api/games";
import { imageProxyUrl } from "./hosting";

// 注意：/cdn-img 只代理到一个 CDN（默认 image.jzbro.cn），所以这里只放跟它同源
// 路径的 host。profile.jzbro.cn 虽然测试时同路径下也 200，但不保证全量映射，
// 留给浏览器直链（它有有效证书，不会有混合内容问题）。
const CDN_HOSTS = new Set([
  "image.cloud.cgaapp.com", // 旧生产（证书过期，必须代理）
  "image.jzbro.cn", // jzbro 测试主图床
]);

/** 把后端图片 URL 改写成本地代理路径；空 / 非该 CDN 的 URL 原样返回 */
function rewriteCdn(url: string | null | undefined, prefix: string): string {
  if (!url) return "";
  try {
    const u = new URL(url);
    if (CDN_HOSTS.has(u.hostname)) {
      return imageProxyUrl(`${prefix}${u.pathname}${u.search}`);
    }
    return url;
  } catch {
    return url;
  }
}

export function cdnImg(url?: string | null): string {
  return rewriteCdn(url, "/cdn-img");
}

/** Card/list images use a resized edge variant instead of full originals. */
export function cdnThumb(url?: string | null): string {
  return rewriteCdn(url, "/cdn-img-thumb");
}

/** 从 pics 数组挑指定 position_type 的图；找不到返回第一张 */
function pickPic(pics: Pic[] | undefined, ...prefer: number[]): string {
  if (!pics?.length) return "";
  for (const pt of prefer) {
    const hit = pics.find((p) => p.position_type === pt && p.img_type === 1);
    if (hit?.url) return hit.url;
  }
  return pics.find((p) => p.img_type === 1)?.url ?? "";
}

/** 列表 / 网格卡片用的小封面（竖版 logo 优先） */
export function gameCover(game: Game | null | undefined): string {
  if (!game) return "";
  // 优先用直接字段（recommend / game_list_1 section 走这条）
  if (game.image) return cdnThumb(game.image);
  // game 推荐位走 pics: 2=小 logo 优先，其次 1=原图
  return cdnThumb(pickPic(game.pics, 2, 1));
}

/** Landscape card thumbnail, capped at 640px by nginx. */
export function gameThumb(game: Game | null | undefined): string {
  if (!game) return "";
  if (game.image_large) return cdnThumb(game.image_large);
  const raw = pickPic(game.pics, 3, 4, 1, 2);
  return raw ? cdnThumb(raw) : gameCover(game);
}

/** 详情 hero / banner 用的大图（横版优先） */
export function gameHero(game: Game | null | undefined): string {
  if (!game) return "";
  if (game.image_large) return cdnImg(game.image_large);
  // 详情 hero 优先 3/4=横版 cover_big，其次 1=原图，最后退到小 logo
  return cdnImg(pickPic(game.pics, 3, 4, 1, 2));
}

/** 详情页截图集（position_type=100） */
export function gameScreenshots(game: Game | null | undefined): string[] {
  if (!game?.pics?.length) return [];
  return game.pics
    .filter((p) => p.position_type === 100 && p.img_type === 1)
    .map((p) => cdnImg(p.url));
}
